/**
 * PHI Detection Service
 * 
 * Detects and handles Protected Health Information (PHI) in documents.
 * Used for HIPAA compliance and data privacy.
 */

/**
 * Common PHI patterns for detection
 */
const PHI_PATTERNS = {
  // Social Security Number (SSN)
  ssn: /\b\d{3}-?\d{2}-?\d{4}\b/g,
  
  // Medical Record Number (MRN) - various formats
  mrn: /\bMRN[:\s]*[A-Z0-9]{6,12}\b/gi,
  
  // Date of Birth (DOB) - various formats
  dob: /\b(?:DOB|Date of Birth|Born)[:\s]*(\d{1,2}[/-]\d{1,2}[/-]\d{2,4})\b/gi,
  
  // Phone numbers
  phone: /\b(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g,
  
  // Email addresses
  email: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
  
  // Credit card numbers (sometimes in medical records)
  creditCard: /\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/g,
  
  // IP addresses
  ipAddress: /\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/g,
  
  // Medical device serial numbers
  deviceSerial: /\b(?:Serial|SN|S\/N)[:\s]*[A-Z0-9]{8,}\b/gi,
};

/**
 * Redaction patterns (what to replace PHI with)
 */
const REDACTION_PLACEHOLDERS = {
  ssn: '[REDACTED-SSN]',
  mrn: '[REDACTED-MRN]',
  dob: '[REDACTED-DOB]',
  phone: '[REDACTED-PHONE]',
  email: '[REDACTED-EMAIL]',
  creditCard: '[REDACTED-CC]',
  ipAddress: '[REDACTED-IP]',
  deviceSerial: '[REDACTED-SERIAL]',
};

/**
 * Detect PHI in text
 * @param {string} text - Text to scan
 * @returns {Object} Detection results with found PHI types and locations
 */
export const detectPHI = (text) => {
  const detected = {
    hasPHI: false,
    phiTypes: [],
    locations: [],
    redactedText: text,
  };

  Object.entries(PHI_PATTERNS).forEach(([type, pattern]) => {
    const matches = [...text.matchAll(pattern)];
    if (matches.length > 0) {
      detected.hasPHI = true;
      if (!detected.phiTypes.includes(type)) {
        detected.phiTypes.push(type);
      }
      
      matches.forEach(match => {
        detected.locations.push({
          type,
          value: match[0],
          index: match.index,
          length: match[0].length,
        });
      });
      
      // Redact this PHI type
      detected.redactedText = detected.redactedText.replace(
        pattern,
        REDACTION_PLACEHOLDERS[type] || '[REDACTED]'
      );
    }
  });

  return detected;
};

/**
 * Check if text contains PHI
 * @param {string} text - Text to check
 * @returns {boolean}
 */
export const containsPHI = (text) => {
  return detectPHI(text).hasPHI;
};

/**
 * Redact PHI from text
 * @param {string} text - Text to redact
 * @returns {string} Redacted text
 */
export const redactPHI = (text) => {
  return detectPHI(text).redactedText;
};

/**
 * Get PHI summary for a document
 * @param {string} text - Document text
 * @returns {Object} Summary of PHI found
 */
export const getPHISummary = (text) => {
  const detection = detectPHI(text);
  return {
    hasPHI: detection.hasPHI,
    phiTypes: detection.phiTypes,
    phiCount: detection.locations.length,
    recommendation: detection.hasPHI 
      ? 'Document contains PHI. Consider redacting before embedding or ensure proper access controls.'
      : 'No PHI detected.',
  };
};

const phiDetectionService = {
  detectPHI,
  containsPHI,
  redactPHI,
  getPHISummary,
};

export default phiDetectionService;
